/**
 * Resolves public (unauthenticated) route prefixes from the BFF at runtime.
 * Adding a new anonymous route requires only a manifest change — no SPA rebuild.
 */

let prefixes: readonly string[] = [];
let loaded = false;
let inflight: Promise<readonly string[]> | null = null;

export function fetchPublicRoutes(): Promise<readonly string[]> {
  if (loaded) return Promise.resolve(prefixes);
  if (inflight) return inflight;

  inflight = fetch('/api/plugins/public-routes')
    .then(r => (r.ok ? r.json() : []))
    .then((data: unknown) => {
      prefixes = Array.isArray(data)
        ? data.filter((s): s is string => typeof s === 'string')
        : [];
      loaded = true;
      return prefixes;
    })
    .catch(() => {
      loaded = true; // fail-closed: empty list = all routes require auth
      return prefixes;
    });

  return inflight;
}

export function isPublicRoute(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return prefixes.some(prefix => p.startsWith(prefix));
}

/** Sync check using manifest-declared public routes (no BFF fetch required). */
export function isManifestPublicPath(
  pathname: string,
  routes?: ReadonlyArray<{ public?: boolean; path?: string }>,
): boolean {
  if (!routes?.length) return false;
  return routes.some(r => r.public === true && !!r.path && pathname.startsWith(r.path));
}
